/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable max-classes-per-file */
/* eslint-disable no-cond-assign */
/* eslint-disable no-restricted-syntax */
/* eslint-disable max-len */

// ---------------------------------------------------------------------------------
// Value provider interface, used to inject different types of  var/vals formats
// ---------------------------------------------------------------------------------
interface IValueProvider {
  getValue(group: string): { folder: string | null, leaf: string } | null;
}
// optionTree based provider
class OptionMapProvider implements IValueProvider {
  constructor(private readonly optionMap: Map<string, { folder: string | null, leaf: string }>) {}

  getValue(group: string) {
    return this.optionMap.get(group) || null;
  }
}
// custom attribute based provider
class AttrListProvider implements IValueProvider {
  constructor(private readonly attrs: { attributeName: string; attributeValue: string }[]) {}

  getValue(group: string) {
    const entry = this.attrs.find((a) => a.attributeName === group);
    if (!entry) return null;

    const parts = entry.attributeValue.split('/');
    if (parts.length === 1) {
      return { folder: null, leaf: parts[0].trim() };
    }
    return { folder: parts[0].trim(), leaf: parts.slice(1).join('/').trim() };
  }
}

// ----------------------------------------------------------------------------------
// Node Types to support each operator type: ! (Not) , && (And) , || (Or)
// ----------------------------------------------------------------------------------
abstract class ConditionNode {
  abstract evaluate(provider: IValueProvider): boolean;
}

class ComparisonNode extends ConditionNode {
  constructor(
    public readonly group: string,
    public readonly operator: '==' | '!=',
    public readonly values: string[],
  ) { super(); }

  public evaluate(provider: IValueProvider): boolean {
    const current = provider.getValue(this.group);
    if (!current) return this.operator === '!=';

    const isMatch = this.values.some((pattern) => ConditionEvaluator.matchPattern(pattern, current.folder, current.leaf));

    return this.operator === '==' ? isMatch : !isMatch;
  }
}

class NotNode extends ConditionNode {
  constructor(public readonly child: ConditionNode) { super(); }

  public evaluate(provider: IValueProvider): boolean {
    return !this.child.evaluate(provider);
  }
}

class AndNode extends ConditionNode {
  constructor(public readonly children: ConditionNode[]) { super(); }

  public evaluate(provider: IValueProvider): boolean {
    return this.children.every((child) => child.evaluate(provider));
  }
}

class OrNode extends ConditionNode {
  constructor(public readonly children: ConditionNode[]) { super(); }

  evaluate(provider: IValueProvider): boolean {
    return this.children.some((child) => child.evaluate(provider));
  }
}

// -----------------------------------------------------------------------------------
// Condition expression evaluator supporting:
//   () - any level of parethesis (for reasonable users... not +oo)
//   !  - not unary operator
//   && - AND binary operator
//   || - OR binary operator
//   LHS - option group name
//   RHS - option value(s) supporting glob (in the folder or leaf)
//   != - option group (LHS) is not set to any of the values (RHS)
//   == - option group (LHS) is set to one of the values (RHS)
//
//  Examples:
//         "Leather 2 == ['*/P 02 Conhaque Cognac' ]"
//         "!(Leather 2 != ['*/P 02 Conhaque Cognac' ])"
//         "(Leather 2 == ['*/P 02 Conhaque Cognac' ]) && (Leather 1 == ['*/P 02 Conhaque Cognac' ])"
// -----------------------------------------------------------------------------------
export class ConditionEvaluator {

  public static evaluateIfCondition(condition: string, optionValues: string[]): boolean {
    const optionMap = this.parseOptionValues(optionValues);
    const tokens = this.tokenize(condition);
    const ast = this.parseExpression(tokens);
    return ast.evaluate(new OptionMapProvider(optionMap));
  }

  public static evaluateAttrCondition(
    condition: string,
    customAttrValues: { attributeName: string; attributeValue: string }[],
  ): boolean {
    const tokens = this.tokenize(condition);
    const ast = this.parseExpression(tokens);
    return ast.evaluate(new AttrListProvider(customAttrValues));
  }

  private static parseOptionValues(optionValues: string[]): Map<string, { folder: string | null, leaf: string }> {
    const map = new Map<string, { folder: string | null, leaf: string }>();

    for (const opt of optionValues) {
      const parts = opt.split('/').map((p) => p.trim());

      if (parts.length === 2) {
        const group = parts[0];
        const leaf = parts[1];

        map.set(group, { folder: null, leaf });
      } else if (parts.length >= 3) {
        const group = parts[0];
        const folder = parts[1];
        const leaf = parts.slice(2).join('/').trim();

        map.set(group, { folder, leaf });

        // Allows conditions like:
        // Leather 1/Elastic == ['P 45 Brush Brown CUSTO EXTRA']
        map.set(`${group}/${folder}`, { folder: null, leaf });
      }
    }

    return map;
  }

  private static tokenize(input: string): string[] {
    const regex = /\s*(==|!=|&&|\|\||!|\(|\))\s*|('[^']*'|\[[^\]]*\]|\S+)/g;
    const tokens: string[] = [];
    let match;
    while ((match = regex.exec(input)) !== null) {
      if (match[1]) {
        tokens.push(match[1]); // operators and parentheses
      } else if (match[2]) {
        tokens.push(match[2]); // strings, arrays, or single tokens
      }
    }
    return tokens;
  }

  private static parseExpression(tokens: string[]): ConditionNode {
    let index = 0;

    function parsePrimary(): ConditionNode {
      const token = tokens[index++];

      if (token === '!') {
        return new NotNode(parsePrimary());
      }

      if (token === '(') {
        const node = parseOr(); // parse full expression inside parentheses
        if (tokens[index++] !== ')') throw new Error('Mismatched parentheses');
        return node;
      }

      index--; // step back: current token wasn't handled as ! or (
      return parseComparison(); // parse a basic comparison
    }

    function parseComparison(): ConditionNode {
      const lhsParts: string[] = [];

      // Safely collect group name tokens until we hit == or !=
      while (index < tokens.length && tokens[index] !== '==' && tokens[index] !== '!=') {
        lhsParts.push(tokens[index++]);
      }

      if (index >= tokens.length) {
        throw new Error(`Comparison operator (== or !=) missing after group "${lhsParts.join(' ')}"`);
      }

      const group = lhsParts.join(' ').trim();

      const operator = tokens[index++] as '==' | '!=';

      // Collect RHS (value or array)
      const rhsParts: string[] = [];
      let bracketDepth = 0;

      while (index < tokens.length) {
        const token = tokens[index];

        if (token === '&&' || token === '||' || token === ')' || token === '(') {
          if (bracketDepth === 0) break;
        }

        if (token.includes('[')) bracketDepth++;
        if (token.includes(']')) bracketDepth--;

        rhsParts.push(tokens[index++]);
      }

      if (rhsParts.length === 0) {
        throw new Error(`Missing RHS value for comparison "${group} ${operator} ?"`);
      }

      const rhsRaw = rhsParts.join(' ').trim();

      let values: string[];
      if (rhsRaw.startsWith('[')) {
        const jsonReady = rhsRaw.replace(/'/g, '"');
        values = JSON.parse(jsonReady).map((s: string) => s.trim());
      } else {
        values = [rhsRaw.replace(/^'(.*)'$/, '$1').trim()];
      }

      return new ComparisonNode(group, operator, values);
    }

    function parseAnd(): ConditionNode {
      const nodes = [parsePrimary()];
      while (tokens[index] === '&&') {
        index++;
        nodes.push(parsePrimary());
      }
      return nodes.length === 1 ? nodes[0] : new AndNode(nodes);
    }

    function parseOr(): ConditionNode {
      const nodes = [parseAnd()];
      while (tokens[index] === '||') {
        index++;
        nodes.push(parseAnd());
      }
      return nodes.length === 1 ? nodes[0] : new OrNode(nodes);
    }

    return parseOr();
  }

  public static matchPattern(pattern: string, folder: string | null, leaf: string): boolean {
    const parts = pattern.split('/');
    let patternFolder: string | null = null;
    let patternLeaf: string;

    if (parts.length === 1) {
      patternLeaf = parts[0].trim();
    } else {
      patternFolder = parts[0].trim();
      patternLeaf = parts.slice(1).join('/').trim();
    }

    const folderMatches = (!patternFolder || patternFolder === '*' || patternFolder === folder);
    const leafMatches = (patternLeaf === '*' || patternLeaf === leaf);

    return folderMatches && leafMatches;
  }

  private static dependsOnNode(node: ConditionNode, name: string): boolean {
    if (node instanceof ComparisonNode) {
      return node.group === name;
    }

    if (node instanceof NotNode) {
      return this.dependsOnNode(node.child, name);
    }

    if (node instanceof AndNode || node instanceof OrNode) {
      return node.children.some((child) => this.dependsOnNode(child, name));
    }

    return false;
  }

  public static dependsOn(condition: string, varName: string): boolean {
    const tokens = this.tokenize(condition);
    const ast = this.parseExpression(tokens);
    return this.dependsOnNode(ast, varName);
  }

}
